customElements.component = 'collection-filters';

if (!customElements.get(customElements.component)) {
    class CollectionFilters extends HTMLElement {
        static isChromeBrowser(userAgent) {
            return /Chrome|CriOS/.test(userAgent);
        }

        static isFirefoxBrowser(userAgent) {
            return /Firefox|FxiOS/.test(userAgent);
        }

        static isUnsupportedVariant(userAgent) {
            return /(Edg|OPR|SamsungBrowser|YaBrowser|Vivaldi|Brave)/.test(userAgent);
        }

        static shouldUseDefaultPagination() {
            const userAgent = navigator.userAgent || '';
            return !CollectionFilters.isUnsupportedVariant(userAgent) &&
                (CollectionFilters.isChromeBrowser(userAgent) || CollectionFilters.isFirefoxBrowser(userAgent));
        }

        /**
         * Collection filters web component.
         *
         * @constructor
         */
        constructor() {
            super();
            this.paginationModes = {
                NUMERIC: 'numeric',
                LOAD_MORE: 'load-more',
                INFINITE: 'infinite'
            };

            this.isSearchPage = /(^|\/)search(\/|$)/.test(window.location.pathname);
            if (this.isSearchPage) {
                this.paginationMode = CollectionFilters.shouldUseDefaultPagination()
                    ? this.paginationModes.NUMERIC
                    : this.paginationModes.LOAD_MORE;
            } else {
                this.paginationMode = this.paginationModes.INFINITE;
            }

            this.infiniteAutoLoadLimit = 2;
            this.autoLoadCount = 0;
            this.productsPerPage = 32;
            this.sparsePageThreshold = 16;
            this.maxPagesPerBatch = 5;
            this.infiniteObserver = null;
            this.infiniteSentinel = null;
            this.infiniteLoader = null;
            this.infiniteLoaderStatus = null;
            this.reachedEnd = false;
            this.loadGeneration = 0;
            this.loadMoreAbortController = null;

            this.initEvents();
            this.initResetButton();
            this.loading = false;
            this.loader = null;
            this.productsCountEl = null;
            this.originalSearchParams = null;

            this.automaticApplyAvailabilityFilter = this.getAttribute("data-automatic-apply-availability-filter") === "true" ? true : false;

            window.addEventListener("DOMContentLoaded", () => {
                this.loader = document.querySelector('.collection .loader');
                this.productsCountEl = document.querySelector('[data-collection-products-count]');
                this.initFiltersCounter();
                this.keepActiveDropdownsOpen();
                this.updateSearchInputVisibility();
                this.storeOriginalSearchParams();
                this.initInfiniteScroll();
            })

            window.addEventListener('pageshow', (event) => {
                if (event.persisted) {
                    this.handleBfcacheRestore();
                }
            });

            window.addEventListener('pagehide', () => this.saveInfiniteState());
        }

        initEvents() {
            this.syncPaginationControls();
            this.loadMoreButton = document.querySelector('.js-collection-load-more');

            this.querySelectorAll('[data-filter-submit]').forEach(submit => {
                submit.addEventListener('click', this.onInputChange.bind(this));
            });

            if (
                (this.paginationMode === this.paginationModes.LOAD_MORE ||
                    this.paginationMode === this.paginationModes.INFINITE) &&
                this.loadMoreButton
            ) {
                this.loadMoreButton.addEventListener('click', this.loadMore.bind(this));
                this.loadMoreButton.dataset.lmBound = 'true';
            }
        }

        syncPaginationControls() {
            const loadMoreContainer = document.querySelector('.collection__load-more');
            const loadMoreButton = document.querySelector('.js-collection-load-more');
            const numericPagination = document.querySelector('.js-collection-pagination');

            if (this.paginationMode === this.paginationModes.NUMERIC) {
                if (loadMoreContainer) {
                    loadMoreContainer.classList.add('hidden');
                }
                if (loadMoreButton) {
                    loadMoreButton.classList.add('hidden');
                    loadMoreButton.setAttribute('aria-hidden', 'true');
                    loadMoreButton.disabled = true;
                }
                if (numericPagination) {
                    numericPagination.classList.remove('hidden');
                }
                return;
            }

            if (this.paginationMode === this.paginationModes.INFINITE) {
                if (numericPagination) {
                    numericPagination.classList.add('hidden');
                }
                if (this.autoLoadCount < this.infiniteAutoLoadLimit || this.reachedEnd) {
                    if (loadMoreContainer) {
                        loadMoreContainer.classList.add('hidden');
                    }
                    if (loadMoreButton) {
                        loadMoreButton.classList.add('hidden');
                        loadMoreButton.setAttribute('aria-hidden', 'true');
                        loadMoreButton.disabled = true;
                    }
                }
                return;
            }

            if (loadMoreContainer) {
                loadMoreContainer.classList.remove('hidden');
            }
            if (loadMoreButton) {
                loadMoreButton.classList.remove('hidden');
                loadMoreButton.removeAttribute('aria-hidden');
                loadMoreButton.disabled = false;
            }
            if (numericPagination) {
                numericPagination.classList.add('hidden');
            }
        }

        onInputChange() {
            this.initRender();
        }

        cancelInFlightLoadMore() {
            this.loadGeneration += 1;
            if (this.loadMoreAbortController) {
                this.loadMoreAbortController.abort();
                this.loadMoreAbortController = null;
            }
            this.loading = false;
            this.unobserveSentinel();
            this.hideInfiniteLoader();
        }

        async updateButtonCounters(responseLayout = null) {
            const nextUrl = document.querySelector('.js-collection-load-more')?.dataset.nextUrl;
            const collectionTotalProductsCount = document.querySelector('.collection-total-products-count');
            const gridItems = document.querySelectorAll('#main-collection-product-grid .product-grid__item');
            const collectionCurrentProductsCount = document.querySelector('.collection-current-products-count');
            if (!collectionCurrentProductsCount && !collectionTotalProductsCount) {
                return;
            }
            if (!nextUrl) {
                return;
            }
            const responseNextPage = await fetch(nextUrl);
            const responseNextPageLayout = new DOMParser().parseFromString(await responseNextPage.text(), 'text/html');

            if (collectionTotalProductsCount && responseLayout) {
                collectionTotalProductsCount.innerHTML = +responseLayout.querySelector('filter-count').innerText.trim().split(' ')[0] - gridItems.length;
            }

            if (collectionCurrentProductsCount) {
                collectionCurrentProductsCount.innerHTML = +responseNextPageLayout.querySelectorAll('#main-collection-product-grid .product-grid__item').length;
            }
        }

        async initFiltersCounter() {
            if (window.location.pathname.includes('search')) {
              return
            }

            if (this.paginationMode === this.paginationModes.NUMERIC) {
              return;
            }

            if (this.paginationMode === this.paginationModes.INFINITE) {
              return;
            }

            try {
              const collectionCounter = document.querySelector('#CollectionFilterCounter');
              const urlParams = new URLSearchParams(window.location.search);
              urlParams.delete('sort_by');
              urlParams.delete('page');
              const filterCountUrl = `${window.location.pathname}?section_id=filter-count&${urlParams.toString()}`;
              const response = await fetch(filterCountUrl);
              const responseLayout = new DOMParser().parseFromString(await response.text(), 'text/html');

              collectionCounter.innerHTML = responseLayout.querySelector('filter-count').innerHTML;

              if (this.paginationMode === this.paginationModes.LOAD_MORE) {
                await this.updateButtonCounters(responseLayout);
              }
            } catch (error) {
              console.error(error);
            }
          }

        async loadMore() {
            const grid = document.querySelector('#main-collection-product-grid');
            if (!grid || this.loading) {
                return 0;
            }
            if (parseInt(grid.dataset.page) >= parseInt(grid.dataset.maxPage)) {
                this.reachedEnd = true;
                this.hideLoadMoreButton();
                return 0;
            }

            this.loading = true;
            if (this.paginationMode === this.paginationModes.INFINITE) {
                this.hideLoadMoreButton();
            }
            this.showInfiniteLoader();

            let addedTotal = 0;
            let pagesThisBatch = 0;

            try {
                while (!this.reachedEnd && pagesThisBatch < this.maxPagesPerBatch) {
                    const added = await this.loadPage();
                    if (added === null) {
                        break;
                    }
                    addedTotal += added;
                    pagesThisBatch += 1;

                    if (added > this.sparsePageThreshold) {
                        break;
                    }
                }
            } finally {
                this.loading = false;
                this.hideInfiniteLoader();
                if (!this.reachedEnd && this.shouldShowFallbackButton()) {
                    this.revealLoadMoreButton();
                }
            }

            return addedTotal;
        }

        async loadPage() {
            const grid = document.querySelector('#main-collection-product-grid');
            if (!grid) {
                return null;
            }

            const currentPage = parseInt(grid.dataset.page);
            const maxPage = parseInt(grid.dataset.maxPage);
            if (currentPage >= maxPage) {
                this.reachedEnd = true;
                this.hideLoadMoreButton();
                return 0;
            }

            const nextPage = currentPage + 1;
            const generation = this.loadGeneration;
            this.loadMoreAbortController = new AbortController();

            try {
                const response = await fetch(this.buildPageUrl(nextPage), {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    signal: this.loadMoreAbortController.signal
                });
                const data = await response.text();

                if (generation !== this.loadGeneration) {
                    return null;
                }

                const newContent = new DOMParser().parseFromString(data, 'text/html').body;

                const newContentNextUrl = newContent.querySelector('.js-collection-load-more')?.dataset.nextUrl;
                if (this.loadMoreButton && newContentNextUrl) {
                    this.loadMoreButton.dataset.nextUrl = newContentNextUrl;
                }

                const items = newContent.querySelectorAll('[data-loadmore-item]');
                items.forEach(productCard => grid.appendChild(productCard));

                grid.dataset.page = nextPage;

                if (this.productsCountEl) {
                    this.productsCountEl.textContent = grid.querySelectorAll('.product-grid__item').length;
                }

                if (nextPage >= maxPage) {
                    this.reachedEnd = true;
                    this.hideLoadMoreButton();
                }

                this.saveInfiniteState();
                return items.length;
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error fetching products:', error);
                }
                return null;
            }
        }

        shouldShowFallbackButton() {
            if (this.paginationMode === this.paginationModes.LOAD_MORE) {
                return true;
            }
            return this.paginationMode === this.paginationModes.INFINITE &&
                this.autoLoadCount >= this.infiniteAutoLoadLimit;
        }

        buildPageUrl(page) {
            const url = new URL(window.location.href);
            url.searchParams.set('page', String(page));
            return url.toString();
        }

        getInfiniteStorageKey() {
            const url = new URL(window.location.href);
            url.searchParams.delete('page');
            return `dtp:infinite-scroll:${url.pathname}${url.search}`;
        }

        saveInfiniteState() {
            if (this.paginationMode !== this.paginationModes.INFINITE) {
                return;
            }
            const grid = document.querySelector('#main-collection-product-grid');
            if (!grid) {
                return;
            }
            try {
                sessionStorage.setItem(this.getInfiniteStorageKey(), JSON.stringify({
                    page: parseInt(grid.dataset.page) || 1,
                    scrollY: window.scrollY || window.pageYOffset || 0
                }));
            } catch (error) {
            }
        }

        readInfiniteState() {
            try {
                const raw = sessionStorage.getItem(this.getInfiniteStorageKey());
                return raw ? JSON.parse(raw) : null;
            } catch (error) {
                return null;
            }
        }

        clearInfiniteState() {
            try {
                sessionStorage.removeItem(this.getInfiniteStorageKey());
            } catch (error) {
            }
        }

        setupInfiniteElements(grid) {
            if (this.infiniteSentinel) {
                return;
            }

            this.infiniteLoader = document.createElement('div');
            this.infiniteLoader.className = 'collection__infinite-loader js-collection-infinite-loader';
            this.infiniteLoader.setAttribute('role', 'status');
            this.infiniteLoader.setAttribute('aria-live', 'polite');
            this.infiniteLoader.innerHTML = '<span class="sr-only js-infinite-loader-status"></span><div></div><div></div><div></div><div></div>';
            this.infiniteLoaderStatus = this.infiniteLoader.querySelector('.js-infinite-loader-status');

            this.infiniteSentinel = document.createElement('div');
            this.infiniteSentinel.className = 'js-collection-infinite-sentinel';
            this.infiniteSentinel.setAttribute('aria-hidden', 'true');

            grid.insertAdjacentElement('afterend', this.infiniteLoader);
            this.infiniteLoader.insertAdjacentElement('afterend', this.infiniteSentinel);
        }

        showInfiniteLoader() {
            if (this.paginationMode === this.paginationModes.INFINITE && this.infiniteLoader) {
                this.infiniteLoader.classList.add('visible');
                if (this.infiniteLoaderStatus) {
                    this.infiniteLoaderStatus.textContent = 'Loading more products…';
                }
                const grid = document.querySelector('#main-collection-product-grid');
                if (grid) {
                    grid.setAttribute('aria-busy', 'true');
                }
            }
        }

        hideInfiniteLoader() {
            if (this.infiniteLoader) {
                this.infiniteLoader.classList.remove('visible');
                if (this.infiniteLoaderStatus) {
                    this.infiniteLoaderStatus.textContent = '';
                }
            }
            const grid = document.querySelector('#main-collection-product-grid');
            if (grid) {
                grid.removeAttribute('aria-busy');
            }
        }

        observeSentinel() {
            if (!this.infiniteSentinel || this.reachedEnd) {
                return;
            }
            if (!this.infiniteObserver) {
                this.infiniteObserver = new IntersectionObserver(
                    (entries) => this.onSentinelIntersect(entries),
                    { rootMargin: '400px 0px' }
                );
            }
            this.infiniteObserver.observe(this.infiniteSentinel);
        }

        unobserveSentinel() {
            if (this.infiniteObserver && this.infiniteSentinel) {
                this.infiniteObserver.unobserve(this.infiniteSentinel);
            }
        }

        async onSentinelIntersect(entries) {
            const entry = entries[0];
            if (!entry || !entry.isIntersecting || this.loading || this.reachedEnd) {
                return;
            }
            if (this.autoLoadCount >= this.infiniteAutoLoadLimit) {
                this.stopAutoLoad();
                return;
            }

            const loaded = await this.loadMore();

            if (this.reachedEnd) {
                this.unobserveSentinel();
                return;
            }
            if (!loaded) {
                return;
            }

            this.autoLoadCount += 1;
            if (this.autoLoadCount >= this.infiniteAutoLoadLimit) {
                this.stopAutoLoad();
                return;
            }
            this.unobserveSentinel();
            this.observeSentinel();
        }

        stopAutoLoad() {
            this.unobserveSentinel();
            this.revealLoadMoreButton();
        }

        revealLoadMoreButton() {
            const grid = document.querySelector('#main-collection-product-grid');
            if (grid && parseInt(grid.dataset.page) >= parseInt(grid.dataset.maxPage)) {
                this.hideLoadMoreButton();
                return;
            }
            const container = document.querySelector('.collection__load-more');
            const button = document.querySelector('.js-collection-load-more');
            if (container) {
                container.classList.remove('hidden');
            }
            if (button) {
                button.classList.remove('hidden');
                button.removeAttribute('aria-hidden');
                button.disabled = false;
            }
        }

        hideLoadMoreButton() {
            const container = document.querySelector('.collection__load-more');
            const button = document.querySelector('.js-collection-load-more');
            if (container) {
                container.classList.add('hidden');
            }
            if (button) {
                button.classList.add('hidden');
                button.setAttribute('aria-hidden', 'true');
                button.disabled = true;
            }
        }

        initInfiniteScroll() {
            if (this.paginationMode !== this.paginationModes.INFINITE) {
                return;
            }
            const grid = document.querySelector('#main-collection-product-grid');
            if (!grid) {
                return;
            }

            if ('scrollRestoration' in history) {
                history.scrollRestoration = 'manual';
            }

            this.setupInfiniteElements(grid);
            this.reachedEnd = parseInt(grid.dataset.page) >= parseInt(grid.dataset.maxPage);

            const navEntry = performance.getEntriesByType('navigation')[0];
            const isBackForward = navEntry && navEntry.type === 'back_forward';
            const savedState = this.readInfiniteState();

            if (isBackForward && savedState && savedState.page > parseInt(grid.dataset.page)) {
                this.restoreInfiniteState(savedState);
                return;
            }

            this.clearInfiniteState();
            if (!this.reachedEnd) {
                this.observeSentinel();
            }
        }

        async restoreInfiniteState(savedState) {
            const grid = document.querySelector('#main-collection-product-grid');
            if (!grid) {
                return;
            }

            const maxPage = parseInt(grid.dataset.maxPage);
            const targetPage = Math.min(savedState.page, maxPage);

            this.loading = true;
            this.showInfiniteLoader();
            try {
                let guard = 0;
                while (parseInt(grid.dataset.page) < targetPage && guard < maxPage + 2) {
                    const before = parseInt(grid.dataset.page);
                    const added = await this.loadPage();
                    guard += 1;
                    if (added === null || parseInt(grid.dataset.page) <= before) {
                        break;
                    }
                }
            } finally {
                this.loading = false;
                this.hideInfiniteLoader();
            }

            this.autoLoadCount = this.infiniteAutoLoadLimit;
            if (this.reachedEnd) {
                this.unobserveSentinel();
            } else {
                this.stopAutoLoad();
            }

            this.restoreScrollPosition(savedState.scrollY);
        }

        handleBfcacheRestore() {
            if (this.paginationMode !== this.paginationModes.INFINITE) {
                return;
            }
            const grid = document.querySelector('#main-collection-product-grid');
            if (!grid) {
                return;
            }

            this.reachedEnd = parseInt(grid.dataset.page) >= parseInt(grid.dataset.maxPage);
            this.autoLoadCount = this.infiniteAutoLoadLimit;
            if (this.reachedEnd) {
                this.unobserveSentinel();
            } else {
                this.stopAutoLoad();
            }

            const savedState = this.readInfiniteState();
            if (savedState) {
                this.restoreScrollPosition(savedState.scrollY);
            }
        }

        restoreScrollPosition(targetY) {
            if (typeof targetY !== 'number' || targetY <= 0) {
                return;
            }
            window.scrollTo(0, targetY);
            requestAnimationFrame(() => window.scrollTo(0, targetY));
        }

        resetInfiniteScroll() {
            if (this.paginationMode !== this.paginationModes.INFINITE) {
                return;
            }
            const grid = document.querySelector('#main-collection-product-grid');
            if (!grid) {
                return;
            }
            this.autoLoadCount = 0;
            this.reachedEnd = parseInt(grid.dataset.page) >= parseInt(grid.dataset.maxPage);
            this.clearInfiniteState();
            this.hideLoadMoreButton();
            this.unobserveSentinel();
            if (!this.reachedEnd) {
                this.observeSentinel();
            }
        }

        getParamsFromForm(form) {
            const formData = new FormData(form);

            if (this.automaticApplyAvailabilityFilter) {
                if (formData.getAll("filter.v.option.size").length) {
                    formData.set("filter.v.availability", "1");
                } else {
                    formData.delete("filter.v.availability");
                }
            }

            return new URLSearchParams(formData).toString();
        }

        initRender() {
            this.loader = document.querySelector('.collection .loader');
            this.loader && this.loader.classList.add("visible");
            const searchParams = this.getParamsFromForm(document.querySelector('#CollectionFiltersForm'));
            const url = `${window.location.pathname}?${searchParams}`;

            const grid = document.querySelector('#main-collection-product-grid');
            if (grid) {
                grid.dataset.page = '1';
            }

            this.renderPage(url, searchParams);
        }

        storeOriginalSearchParams() {
            if (window.location.pathname.includes('search')) {
                const urlParams = new URLSearchParams(window.location.search);
                const originalParams = new URLSearchParams();

                for (const [key, value] of urlParams) {
                    if (!key.startsWith('filter.') &&
                        !key.startsWith('filter_') &&
                        !key.startsWith('filter[') &&
                        key !== 'page' &&
                        key !== 'sort_by') {
                        originalParams.set(key, value);
                    }
                }

                this.originalSearchParams = originalParams.toString();
            }
        }

        resetFilters() {
            this.clearFilterCheckboxes();

            if (window.location.pathname.includes('search')) {
                if (this.originalSearchParams) {
                    const newUrl = `${window.location.pathname}?${this.originalSearchParams}`;
                    this.renderPage(newUrl, this.originalSearchParams);
                } else {
                    const urlParams = new URLSearchParams(window.location.search);
                    const preservedParams = new URLSearchParams();

                    for (const [key, value] of urlParams) {
                        if (!key.startsWith('filter.') &&
                            !key.startsWith('filter_') &&
                            !key.startsWith('filter[') &&
                            key !== 'page' &&
                            key !== 'sort_by') {
                            preservedParams.set(key, value);
                        }
                    }

                    const preservedQueryString = preservedParams.toString();
                    const newUrl = preservedQueryString ?
                        `${window.location.pathname}?${preservedQueryString}` :
                        window.location.pathname;

                    this.renderPage(newUrl, preservedQueryString);
                }
            } else {
                this.renderPage(window.location.pathname, "");
            }
        }

        clearFilterCheckboxes() {
            const checkboxes = document.querySelectorAll('#CollectionFiltersForm input[type="checkbox"]');
            checkboxes.forEach(checkbox => {
                checkbox.checked = false;
            });

            const numberInputs = document.querySelectorAll('#CollectionFiltersForm input[type="number"]');
            numberInputs.forEach(input => {
                input.value = '';
            });

            const activeFilterItems = document.querySelectorAll('[data-filter-group] li.selected');
            activeFilterItems.forEach(item => {
                item.classList.remove('selected');
            });
        }

        updateFilterDrawer(html) {
            const newDocument = new DOMParser().parseFromString(html, 'text/html');
            const newFilterForm = newDocument.querySelector('#CollectionFiltersForm');
            const currentFilterForm = document.querySelector('#CollectionFiltersForm');

            if (newFilterForm && currentFilterForm) {
                currentFilterForm.innerHTML = newFilterForm.innerHTML;
                this.keepActiveDropdownsOpen();
            }
        }

        keepActiveDropdownsOpen() {
            const detailsElements = document.querySelectorAll('#CollectionFiltersForm details');

            detailsElements.forEach(details => {
                const checkedCheckboxes = details.querySelectorAll('input[type="checkbox"]:checked');
                const filledNumberInputs = details.querySelectorAll('input[type="number"]:not([value=""])');

                if (checkedCheckboxes.length > 0 || filledNumberInputs.length > 0) {
                    details.open = true;
                }
            });
        }

        renderPage(url, searchParams) {
            this.cancelInFlightLoadMore();
            window.scrollTo(0, 130);

            fetch(url)
                .then((response) => response.text())
                .then((responseText) => {
                    const html = responseText;
                    this.updateURLHash(searchParams);
                    this.renderGrid(html);
                    this.updateFilterDrawer(html);
                    this.initResetButton();
                    this.initFiltersCounter();
                });
        }

        renderGrid(html) {
            const newDocument = new DOMParser().parseFromString(html, 'text/html');
            const newGrid = newDocument.querySelector('#main-collection-product-grid');
            const noProducts = document.querySelector('.js-collection-no-products');
            const newNoProducts = newDocument.querySelector('.js-collection-no-products');
            const currentGrid = document.querySelector('#main-collection-product-grid');
            const collectionSeo = document.querySelector('.collection__seo');

            this.updateFilterCheckboxes(newDocument);

            if (newGrid && currentGrid) {
                currentGrid.innerHTML = newGrid.innerHTML;
                currentGrid.dataset.page = newGrid.dataset.page;
                currentGrid.dataset.maxPage = newGrid.dataset.maxPage;
                currentGrid.classList.remove('hidden');
                if (collectionSeo) collectionSeo.classList.remove('hidden');
                if (noProducts) noProducts.classList.add('hidden');
            }

            if (newNoProducts) {
                if (noProducts) {
                    noProducts.remove();
                }

                const container = currentGrid ? currentGrid.parentNode : document.querySelector('#CollectionProductGrid');
                if (container) {
                    container.appendChild(newNoProducts);
                }

                if (currentGrid) currentGrid.classList.add('hidden');
                if (collectionSeo) collectionSeo.classList.add('hidden');

                this.hidePagination();
            } else {
                this.showPagination();
            }

            const newCounter = newDocument.querySelector('#CollectionFilterCounter');
            const currentCounter = document.querySelector('#CollectionFilterCounter');

            if (newCounter && currentCounter) {
                currentCounter.innerHTML = newCounter.innerHTML;
            }

            this.updateSearchCount(html);

            this.loader && this.loader.classList.remove("visible");

            this.keepActiveDropdownsOpen();
            this.updateSearchInputVisibility();
            this.syncLoadMoreContainer(newDocument);
            this.resetInfiniteScroll();
            this.syncPaginationControls();
        }

        syncLoadMoreContainer(newDocument) {
            if (this.paginationMode !== this.paginationModes.INFINITE) {
                return;
            }
            const grid = document.querySelector('#main-collection-product-grid');
            const newContainer = newDocument.querySelector('.collection__load-more');
            const currentContainer = document.querySelector('.collection__load-more');

            if (newContainer && !currentContainer && grid) {
                const imported = document.importNode(newContainer, true);
                const anchor = this.infiniteSentinel || grid;
                anchor.insertAdjacentElement('afterend', imported);
            } else if (!newContainer && currentContainer) {
                currentContainer.classList.add('hidden');
            }

            this.loadMoreButton = document.querySelector('.js-collection-load-more');
            if (this.loadMoreButton && this.loadMoreButton.dataset.lmBound !== 'true') {
                this.loadMoreButton.addEventListener('click', this.loadMore.bind(this));
                this.loadMoreButton.dataset.lmBound = 'true';
            }
        }

        updateFilterCheckboxes(newDocument) {
            const newCheckboxes = newDocument.querySelectorAll('#CollectionFiltersForm input[type="checkbox"]');

            newCheckboxes.forEach(newCheckbox => {
                const currentCheckbox = document.querySelector(`#CollectionFiltersForm input[name="${newCheckbox.name}"][value="${newCheckbox.value}"]`);
                if (currentCheckbox) {
                    currentCheckbox.checked = newCheckbox.checked;
                }
            });

            const newActiveItems = newDocument.querySelectorAll('[data-filter-group] li.selected');
            const currentActiveItems = document.querySelectorAll('[data-filter-group] li.selected');

            currentActiveItems.forEach(item => {
                item.classList.remove('selected');
            });

            newActiveItems.forEach(newItem => {
                const group = newItem.parentNode.parentNode.dataset.filterGroup;
                const value = newItem.querySelector('[data-filter-list-option-link]')?.textContent?.trim();

                if (group && value) {
                    const currentItem = document.querySelector(`[data-filter-group="${group}"] [data-filter-list-option-link]:contains("${value}")`)?.parentNode;
                    if (currentItem) {
                        currentItem.classList.add('selected');
                    }
                }
            });
        }

        updateSearchInputVisibility() {
            if (!window.location.pathname.includes('search')) {
                return;
            }

            const searchInputContainer = document.querySelector('.search-page .w-full.md\\:w-1\\/2.md\\:mx-auto');
            const noProductsElement = document.querySelector('.js-collection-no-products');
            const productGrid = document.querySelector('#main-collection-product-grid');

            if (searchInputContainer) {
                if (noProductsElement && !noProductsElement.classList.contains('hidden')) {
                    searchInputContainer.classList.remove('hidden');
                } else if (productGrid && productGrid.querySelectorAll('.product-grid__item').length === 0) {
                    searchInputContainer.classList.remove('hidden');
                } else {
                    searchInputContainer.classList.add('hidden');
                }
            }
        }

        updateSearchCount(html) {
            if (!window.location.pathname.includes('search')) {
                return;
            }

            const newDocument = new DOMParser().parseFromString(html, 'text/html');
            const newSearchCount = newDocument.querySelector('.js-search-count');
            const currentSearchCount = document.querySelector('.js-search-count');

            if (newSearchCount && currentSearchCount) {
                currentSearchCount.textContent = newSearchCount.textContent;
            }
        }

        hidePagination() {
            const pagination = document.querySelector('.pagination');
            const searchFooter = document.querySelector('.search__footer');

            if (pagination) {
                pagination.style.display = 'none';
            }
            if (searchFooter) {
                searchFooter.style.display = 'none';
            }
        }

        showPagination() {
            const pagination = document.querySelector('.pagination');
            const searchFooter = document.querySelector('.search__footer');

            if (pagination) {
                pagination.style.display = '';
            }
            if (searchFooter) {
                searchFooter.style.display = '';
            }
        }

        updateURLHash(searchParams) {
            history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
        }

        initResetButton() {
            const resetButton = document.querySelector("#resetFilters");
            const resetButtonMobile = document.querySelector('[data-filter-reset]');

            if (resetButton) {
                resetButton.removeEventListener("click", this.resetFilters);
                resetButton.addEventListener("click", () => this.resetFilters());
            }
            if (resetButtonMobile) {
                resetButtonMobile.removeEventListener("click", this.resetFilters);
                resetButtonMobile.addEventListener("click", () => this.resetFilters());
            }
        }
    }

    customElements.define('collection-filters', CollectionFilters);
}
